'use client';

import { type FC } from 'react';
import { type ButtonProps } from '../Button/Button';
import { CircularProgress } from '../CircularProgress/CircularProgress';
import {
  ColorButtonTooltip,
  ColorButtonTooltipProvider,
  ColorButtonTooltipTrigger,
  ColorButtonTooltipContent,
  ColorButtonTooltipArrow,
  ColorButtonTooltipPortal,
} from './ColorButtonTooltip/ColorButtonTooltip';

export type ColorButtonProps = {
  /** color name */
  name: string;
  /** color value for css background color */
  color?: string;
  /** color (pattern) image url */
  image?: string;
  /** should the image be fitted into the button
   * @default false
   */
  fitBackgroundImage?: boolean;
  /** if color is active
   * @default false
   */
  active?: boolean;
  /** size for color button
   * @default small
   */
  size?: 'small' | 'medium' | 'large';
  /** rounded or square color button
   * @default false
   */
  square?: boolean;
  /** if the button is selected
   * @default false
   */
  isSelected?: boolean;
} & Omit<ButtonProps, 'color' | 'children'>;

const sizeClasses = {
  small: 'min-h-[30px] min-w-[30px]',
  medium: 'min-h-[44px] min-w-[44px]',
  large: 'min-h-[60px] min-w-[60px]',
};

/**
 * ColorButton component based on https://mui.com/material-ui/react-button/
 * */
export const ColorButton: FC<ColorButtonProps> = ({
  name,
  color,
  image,
  fitBackgroundImage = false,
  // TODO remove, in favor of `selected` or `isSelected` and an explicit `loading` or `isLoading` prop
  active = false,
  size = 'small',
  square = false,
  isSelected = false,
  disabled,
  onClick,
  className,
  ...rest
}) => {
  const isLoading = isSelected && !active;
  const outlineClass = size === 'small' ? 'outline-2' : 'outline-[3px]';

  return (
    <ColorButtonTooltipProvider delayDuration={0}>
      <ColorButtonTooltip>
        <ColorButtonTooltipTrigger asChild>
          {/*  using html native button to simplify the styles which saves a lot of effort (due to asChild prop, shared UI Button needs more styling to match Mui) */}
          <button
            aria-busy={isLoading ? 'true' : 'false'}
            aria-label={name}
            type="button"
            className={`${active ? `border-color-text-light border-2 ${outlineClass}` : 'border-color-text-darktransparent border-[1px]'} relative ${square ? 'rounded' : 'rounded-full'} grid place-items-center ${sizeClasses[size]} ${className}`}
            onClick={(e) => {
              if (isLoading || isSelected) return;
              e.stopPropagation();
              onClick?.(e);
            }}
            style={{
              background: `url(${image}) center repeat`,
              ...(fitBackgroundImage && { backgroundSize: 'cover' }),
            }}
            disabled={disabled}
            {...rest}
          >
            {isLoading && (
              <>
                <span
                  className={`bg-color-background-base ${square ? 'rounded-sm' : 'rounded-full'} pointer-events-none absolute inset-0 z-10 opacity-80`}
                />
                <CircularProgress
                  className={`pointer-events-none absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 ${sizeClasses[size]}`}
                />
              </>
            )}
          </button>
        </ColorButtonTooltipTrigger>
        <ColorButtonTooltipPortal>
          <ColorButtonTooltipContent side="bottom">
            {name}
            <ColorButtonTooltipArrow className="fill-color-background-base h-1 drop-shadow-md" />
          </ColorButtonTooltipContent>
        </ColorButtonTooltipPortal>
      </ColorButtonTooltip>
    </ColorButtonTooltipProvider>
  );
};
